import { useEffect, useState } from "react";
import { Sparkles, Check, Clock, ShoppingBasket, CalendarDays, Bell } from "lucide-react";
import { api } from "@/lib/api";

const typeMeta = {
  shopping: { label: "Lista spesa", icon: ShoppingBasket, color: "var(--sage)" },
  calendar_event: { label: "Calendario", icon: CalendarDays, color: "var(--info)" },
  reminder: { label: "Promemoria", icon: Bell, color: "var(--amber)" },
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function FeedPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/memo/feed")
      .then(({ data }) => setItems(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const approved = items.filter(i => i.status === "approved");
  const pending = items.filter(i => i.status === "pending");

  return (
    <div className="px-5 md:px-10 py-8 max-w-4xl mx-auto" data-testid="feed-page">
      <div className="flex items-start gap-3 mb-8 animate-fade-up">
        <div className="w-11 h-11 rounded-2xl bg-brand grid place-items-center shrink-0">
          <Sparkles className="w-5 h-5 text-white" strokeWidth={1.8} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-tertiary-fg font-semibold">Il tuo membro virtuale di famiglia</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
            Memo AI<span className="text-brand">.</span>
          </h1>
          <p className="text-secondary-fg text-sm mt-1">Eventi, spese e promemoria che Memo AI ha organizzato per voi.</p>
        </div>
      </div>

      {loading && <div className="text-sm text-tertiary-fg">Caricamento…</div>}

      {!loading && items.length === 0 && (
        <div className="bg-surface border border-subtle rounded-3xl p-10 text-center" data-testid="feed-empty">
          <Sparkles className="w-8 h-8 text-brand mx-auto mb-3" strokeWidth={1.4} />
          <h3 className="font-display text-lg font-medium">Niente da mostrare… per ora</h3>
          <p className="text-sm text-secondary-fg mt-2 max-w-md mx-auto">
            Quando scriverete in chat famiglia, Memo AI suggerirà eventi, spese e promemoria. Tutto ciò che approverete comparirà qui.
          </p>
        </div>
      )}

      {pending.length > 0 && (
        <section className="mb-8" data-testid="feed-pending-section">
          <h2 className="text-xs uppercase tracking-widest text-tertiary-fg font-semibold mb-3 flex items-center gap-2">
            <Clock className="w-3.5 h-3.5" /> In attesa di approvazione · {pending.length}
          </h2>
          <div className="space-y-2">
            {pending.map((s) => <FeedRow key={s.id} item={s} />)}
          </div>
        </section>
      )}

      {approved.length > 0 && (
        <section data-testid="feed-approved-section">
          <h2 className="text-xs uppercase tracking-widest text-tertiary-fg font-semibold mb-3 flex items-center gap-2">
            <Check className="w-3.5 h-3.5" /> Organizzato da Memo AI · {approved.length}
          </h2>
          <div className="space-y-2">
            {approved.map((s) => <FeedRow key={s.id} item={s} />)}
          </div>
        </section>
      )}
    </div>
  );
}

function FeedRow({ item }) {
  const meta = typeMeta[item.type] || { label: item.type, icon: Sparkles, color: "var(--brand)" };
  const Icon = meta.icon;
  const p = item.payload || item.data || {};
  const title = item.title || p.name || p.title || p.text || p.description || "Suggerimento";
  const detail = p.date || p.time || p.quantity || (item.title && p.text && p.text !== item.title ? p.text : "");

  return (
    <div
      data-testid={`feed-item-${item.id}`}
      className="bg-surface border border-subtle rounded-2xl p-4 flex items-start gap-3 animate-fade-up"
    >
      <div className="w-9 h-9 rounded-xl grid place-items-center shrink-0" style={{ backgroundColor: meta.color + "22" }}>
        <Icon className="w-4 h-4" style={{ color: meta.color }} strokeWidth={1.6} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-tertiary-fg font-semibold">{meta.label}</span>
          {item.status === "pending" && (
            <span className="text-[10px] uppercase tracking-widest text-amber-warm font-semibold">In attesa</span>
          )}
        </div>
        <div className="text-sm font-medium mt-0.5 truncate">{title}</div>
        {detail && <div className="text-xs text-tertiary-fg mt-0.5">{detail}</div>}
      </div>
      <div className="text-[11px] text-tertiary-fg shrink-0 font-mono">{formatDate(item.created_at)}</div>
    </div>
  );
}
